import {useSyncExternalStore} from 'react';

export type SystemTheme = 'light' | 'dark';
export const SYSTEM_THEME_QUERY = '(prefers-color-scheme: dark)';
type ThemeMedia = Pick<MediaQueryList, 'matches' | 'addEventListener' | 'removeEventListener'>;

/** One browser preference and listener for every mounted workspace surface. */
export function createSystemThemeStore(media: ThemeMedia | null) {
  const listeners = new Set<() => void>();
  const changed = () => { for (const listener of listeners) listener(); };
  return {
    getSnapshot: (): SystemTheme => media?.matches ? 'dark' : 'light',
    subscribe(listener: () => void) {
      if (!listeners.size) media?.addEventListener('change', changed);
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
        if (!listeners.size) media?.removeEventListener('change', changed);
      };
    },
  };
}

let store: ReturnType<typeof createSystemThemeStore> | undefined;
function systemThemeStore() {
  return store ??= createSystemThemeStore(typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(SYSTEM_THEME_QUERY) : null);
}
export function getSystemTheme(): SystemTheme { return systemThemeStore().getSnapshot(); }
export function useSystemTheme(): SystemTheme {
  return useSyncExternalStore(systemThemeStore().subscribe, systemThemeStore().getSnapshot, () => 'light');
}
/** Called before React mounts; CSS media rules also cover the pre-JavaScript paint. */
export function initializeSystemTheme() {
  const apply = () => { document.documentElement.dataset.theme = getSystemTheme(); };
  apply();
  return systemThemeStore().subscribe(apply);
}
